import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckIcon, CreditCardIcon, HourglassIcon } from 'lucide-react';
import { BookingResponse } from '../../types/booking';
import { contactDetails } from '../../data/house';
import { formatNaira } from '../../utils/format';
import { BrandEmblem } from '../BrandEmblem';
interface BookingResultProps {
  response: BookingResponse;
  onReset: () => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;
export function BookingResult({
  response,
  onReset
}: BookingResultProps) {
  const amounts = [{
    label: 'Accommodation / session',
    value: response.baseAmount
  }, {
    label: 'Early check-in fee',
    value: response.earlyFee
  }, {
    label: 'Late checkout fee',
    value: response.lateFee
  }, {
    label: 'Refundable caution fee',
    value: response.cautionFee
  }].filter(row => typeof row.value === 'number' && row.value as number > 0);
  const currencyNote = response.currency && response.currency !== 'NGN' ? ` (${response.currency})` : '';
  return <motion.section initial={{
    opacity: 0,
    y: 16
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3,
    ease: EASE
  }} className="mx-auto max-w-2xl overflow-hidden rounded-sm bg-ivory-50 shadow-lift" aria-live="polite" data-id="element-30" data-mp="2xqlhvhbtoouo">
      <div className="relative bg-forest-700 px-8 py-10 sm:px-12" data-id="element-31" data-mp="3bpbi35txv4n9">
        <BrandEmblem primary="#F7F4ED" accent="#C6A15B" className="absolute -right-6 -top-4 h-40 w-auto opacity-10" title="" data-id="element-32" data-mp="3bmfjdfa9obxr" />
        <motion.span initial={{
        scale: 0.96,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        duration: 0.25,
        ease: EASE,
        delay: 0.1
      }} className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-gold-400 text-forest-800" data-id="element-33" data-mp="3koyhjm33y0do">
          <CheckIcon size={22} aria-hidden="true" data-id="element-34" data-mp="39i879u44rf6" />
        </motion.span>
        <h2 className="mt-6 font-display text-3xl text-ivory-100" data-id="element-35" data-mp="88310o71c8tl">Booking request received</h2>
        {response.message && <p className="mt-3 text-[15px] leading-relaxed text-ivory-200/80" data-id="element-36" data-mp="3ep23xqcbe4cl">{response.message}</p>}
      </div>

      <div className="px-8 py-8 sm:px-12" data-id="element-37" data-mp="39qqu3fm0rtud">
        <dl className="grid gap-5 sm:grid-cols-3" data-id="element-38" data-mp="39s1ynhnah5mj">
          <Meta label="Booking reference" value={response.bookingId ?? 'Sent by email'} strong data-id="element-39" data-mp="3ofn43i7mzgxm" />
          <Meta label="Booking status" value={response.bookingStatus ?? 'Pending'} data-id="element-40" data-mp="3jnv82s5poj03" />
          <Meta label="Payment status" value={response.paymentStatus ?? 'Unpaid'} data-id="element-41" data-mp="1a5jjudqosp5" />
        </dl>

        <div className="mt-6 flex items-start gap-3 rounded-sm bg-gold-100 p-4 text-[13px] leading-relaxed text-forest-700" data-id="element-42" data-mp="3sgqevqatumrz">
          <HourglassIcon size={16} className="mt-0.5 shrink-0 text-gold-600" aria-hidden="true" data-id="element-43" data-mp="b8v3rtt3yp8k" />
          <p data-id="element-44" data-mp="698qxlh6zb0a">
            Your booking is <strong className="font-medium" data-id="element-45" data-mp="6a1njtmfsk4p">pending</strong> and will only be confirmed once payment has
            been verified. A confirmation email is on its way from our booking system.
          </p>
        </div>

        {(amounts.length > 0 || typeof response.totalAmount === 'number') && <div className="mt-8" data-id="element-46" data-mp="njcm6hz187a9">
            <p className="text-[11px] uppercase tracking-overline text-gold-700" data-id="element-47" data-mp="shxn6jm3yuzr">Price breakdown{currencyNote}</p>
            <ul className="mt-3 divide-y divide-forest-700/10 border-y border-forest-700/10 text-[14px]" data-id="element-48" data-mp="dtauzhbdu8r4">
              {amounts.map(row => <li key={row.label} className="flex justify-between gap-4 py-3" data-id="element-49" data-mp="iqjizyj7p70a">
                  <span className="text-muted" data-id="element-50" data-mp="4ra4r4ekgmyy">{row.label}</span>
                  <span className="text-ink" data-id="element-51" data-mp="8tom1k2l7eo4">{formatNaira(row.value as number)}</span>
                </li>)}
              {typeof response.totalAmount === 'number' && <li className="flex items-baseline justify-between gap-4 py-4" data-id="element-52" data-mp="3vymkdezswscr">
                  <span className="text-[11px] uppercase tracking-overline text-gold-700" data-id="element-53" data-mp="3w0qmpsi1xjax">Total due</span>
                  <span className="font-display text-2xl text-forest-700" data-id="element-54" data-mp="1p63q923dtfbx">{formatNaira(response.totalAmount)}</span>
                </li>}
            </ul>
          </div>}

        <div className="mt-8 rounded-sm border border-dashed border-forest-700/20 p-5" data-id="element-55" data-mp="3q8bitr3cphon">
          <div className="flex items-center gap-3" data-id="element-56" data-mp="3lc4ajrnhfu3j">
            <CreditCardIcon size={18} className="text-forest-500" aria-hidden="true" data-id="element-57" data-mp="3h97xeggpoq6z" />
            <p className="text-[14px] text-forest-700" data-id="element-58" data-mp="3cawoc7y8qjpt">Secure online payment</p>
          </div>
          <p className="mt-2 text-[13px] leading-relaxed text-muted" data-id="element-59" data-mp="37ba073gvjj28">
            Paystack checkout will appear here once it’s connected. Until then, our team will contact you with payment
            details.
          </p>
          <button type="button" disabled className="mt-4 inline-flex cursor-not-allowed items-center gap-2 rounded-full bg-forest-100 px-5 py-2.5 text-[13px] text-forest-400" data-id="element-60" data-mp="2xkyy7mgr3jxz">
            Pay with Paystack — coming soon
          </button>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3" data-id="element-61" data-mp="2jlpgujgt6jsf">
          <a href={contactDetails.whatsappHref} target="_blank" rel="noreferrer" className="inline-flex rounded-full bg-forest-700 px-6 py-3 text-sm text-ivory-100 transition-colors duration-150 ease-premium hover:bg-forest-600" data-id="element-62" data-mp="z3x42gbowocd">
            Message us on WhatsApp
          </a>
          <button type="button" onClick={onReset} className="inline-flex rounded-full border border-forest-700/20 px-6 py-3 text-sm text-forest-700 transition-colors duration-150 ease-premium hover:bg-ivory-200" data-id="element-63" data-mp="1d2od3obp3jtn">
            Make another booking
          </button>
          <Link to="/" className="text-sm text-forest-600 underline underline-offset-4 hover:text-forest-800" data-id="element-64" data-mp="pdbw42a4m7gs">
            Back to home
          </Link>
        </div>
      </div>
    </motion.section>;
}
function Meta({
  label,
  value,
  strong = false
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return <div data-id="element-65" data-mp="1mq059decdn5">
      <dt className="text-[10px] uppercase tracking-overline text-gold-700" data-id="element-66" data-mp="1mg5h8hepib4">{label}</dt>
      <dd className={`mt-1.5 ${strong ? 'break-all font-display text-lg text-forest-700' : 'text-[15px] capitalize text-ink'}`} data-id="element-67" data-mp="14xlz3f243hpg">{value}</dd>
    </div>;
}